import {
  type PropsWithChildren,
  type UIEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import styled, { css } from 'styled-components';
import { CloseBold16 } from '@elfsight/icons';
import { Icon } from '@elfsight/lib-icon';
import { COMMON_MOBILE_BREAKPOINT } from '@elfsight/lib-utils';
import { ellipsisMixin } from '@elfsight/widget-ui';
import { customScrollbarMixin, useIsMobile } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import AccessMan from '../../icons/access-man.svg?react';
import MobileClose from '../../icons/angle-bold-down.svg?react';
import Back from '../../icons/back.svg?react';
import RefreshSettings from '../../icons/refresh-settings.svg?react';
import type { WidgetPage } from '../../types';
import { useA11yState } from '../hooks/use-a11y-state';
import { useDemoToggle } from '../hooks/use-demo-toggle';
import { useNavigation } from '../hooks/use-navigation';
import { usePageContext } from '../hooks/use-page';
import { useSwipe } from '../hooks/use-swipe';
import { HeaderButton } from './header-button';

type PageProps = PropsWithChildren<{
  title: string;
  showBack?: boolean;
  onCloseRequest: WidgetPage['onCloseRequest'];
}>;

export function Page({
  children,
  title,
  showBack = false,
  onCloseRequest
}: PageProps) {
  const { t } = useLingui();
  const { toggleState } = useDemoToggle();
  const { displayFreeLink, renderThumbnailMode, theme, editMode } =
    useSettings();
  const {
    a11yState: { colorsContrast } = {},
    isA11yStateModified,
    resetA11yState
  } = useA11yState();
  const { currentPage, backToMainPage } = useNavigation();
  const { setScrollState } = usePageContext();
  const touchEvents = useSwipe(onCloseRequest);
  const isMobile = useIsMobile();
  const [isAtTop, setIsAtTop] = useState(true);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const withHighContrast =
    colorsContrast === 'high-contrast' &&
    (!editMode || toggleState === 'after');

  const {
    themeSettings: { headerIconColor }
  } = theme;

  const showSeparator = currentPage !== 'main' && !isAtTop;

  const updateScrollState = useCallback(
    (e?: UIEvent<HTMLDivElement> | { currentTarget: HTMLDivElement }) => {
      const target = e?.currentTarget;

      if (!target) {
        return;
      }

      const hasScroll = target.scrollHeight > target.clientHeight;
      const isAtTop = target.scrollTop === 0;
      const isAtBottom =
        hasScroll &&
        Math.abs(target.scrollHeight - target.clientHeight - target.scrollTop) <
          2;

      setIsAtTop(isAtTop);
      setScrollState({ hasScroll, isAtBottom });
    },
    [setScrollState]
  );

  useEffect(() => {
    if (contentRef.current) {
      updateScrollState({ currentTarget: contentRef.current });
    }
  }, [updateScrollState]);

  return (
    <Container
      $displayFreeLink={displayFreeLink}
      $withHighContrast={withHighContrast}
      $renderThumbnailMode={renderThumbnailMode}
    >
      <Header
        {...touchEvents}
        $showSeparator={showSeparator}
        $showBack={showBack}
      >
        {showBack && (
          <HeaderButton
            aria-label={t`Back`}
            tooltipTitle={t`Back`}
            icon={Back}
            onClick={() => backToMainPage()}
            disableBackground
          />
        )}

        <Avatar
          aria-label={t`Accessibility Icon`}
          component={AccessMan}
          size={20}
          role="img"
          fill={headerIconColor}
        />

        <Title role="heading" aria-level={2}>
          {title}
        </Title>

        {isA11yStateModified && (
          <HeaderButton
            aria-label={t`Reset settings`}
            tooltipTitle={t`Reset settings`}
            icon={RefreshSettings}
            onClick={resetA11yState}
          />
        )}
        <HeaderButton
          aria-label={t`Close panel`}
          tooltipTitle={t`Close panel`}
          icon={isMobile ? MobileClose : CloseBold16}
          onClick={onCloseRequest}
        />
      </Header>

      <Content
        $displayFreeLink={displayFreeLink}
        ref={contentRef}
        onScroll={updateScrollState}
      >
        {children}
      </Content>
    </Container>
  );
}

const Container = styled.div<{
  $displayFreeLink?: boolean;
  $withHighContrast?: boolean;
  $renderThumbnailMode?: boolean;
}>`
  ${({
    theme,
    $displayFreeLink,
    $withHighContrast,
    $renderThumbnailMode
  }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 0 8px;
    box-sizing: border-box;
    ${theme.page.background &&
    css`
      background: ${theme.page.background};
    `}

    ${$withHighContrast &&
    css`
      filter: contrast(80%) !important;
    `}

    ${$renderThumbnailMode &&
    css`
      bottom: 72px;
      box-shadow: 0px 6px 30px 0px rgba(0, 0, 0, 0.1);
    `}

    ${$displayFreeLink &&
    css`
      padding-bottom: 38px; //free link height
    `} 
    
    @media (max-width: ${COMMON_MOBILE_BREAKPOINT}px) {
      padding-inline: 0;
    }
  `}
`;

const Header = styled.div<{ $showSeparator: boolean; $showBack: boolean }>`
  ${({ theme, $showSeparator, $showBack }) => css`
    display: flex;
    align-items: center;
    padding: ${$showBack ? '16px 12px' : '16px 12px 16px 20px'};
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
    position: relative;

    &::after {
      content: '';
      opacity: ${$showSeparator ? 1 : 0};
      position: absolute;
      bottom: 0;
      left: -10px;
      width: calc(100% + 20px);
      height: 1px;
      background-color: ${theme.page.separatorColor};
    }

    @media (max-width: ${COMMON_MOBILE_BREAKPOINT}px) {
      padding: 10px 20px;

      &::after {
        opacity: 1;
      }
    }
  `}
`;

const Avatar = styled(Icon)`
  ${({ theme }) => css`
    width: 32px;
    height: 32px;
    background-color: ${theme.header.avatarBackgroundColor};
    border-radius: 50%;
  `}
`;

const Title = styled.div`
  ${({ theme }) => css`
    flex: 1;
    margin-inline: 8px;
    padding-right: 4px;
    color: ${theme.header.titleColor};

    ${ellipsisMixin};
  `}
`;

const Content = styled.div<{
  $displayFreeLink: boolean | undefined;
}>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  padding-bottom: 8px;
  overflow-y: auto;
  overscroll-behavior: contain;
  ${customScrollbarMixin};

  @media (max-width: ${COMMON_MOBILE_BREAKPOINT}px) {
    padding-bottom: 0;

    &:has(> :only-child) {
      padding-bottom: 16px;
    }
  }
`;
